<script setup>
import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import * as authApi from '@/api/auth'
import * as validation from '@/utils/validation'

const router = useRouter()
const step = ref(1) // 1: Verify Phone, 2: Reset Password

const form = ref({
  phone: '',
  code: '',
  newPassword: '',
  confirmPassword: ''
})

// UI state
const loading = ref(false)
const error = ref('')
const codeButtonLoading = ref(false)
const passwordErrors = ref([])

// Countdown logic
const countdown = ref(0)
let timer = null

const sendCode = async () => {
  error.value = ''

  if (!validation.validatePhone(form.value.phone)) {
    error.value = validation.getPhoneErrorMessage()
    return
  }

  codeButtonLoading.value = true
  try {
    const response = await authApi.sendVerificationCode(form.value.phone, 3) // 3 = reset password
    if (response.code === 1) {
      countdown.value = 60
      timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    } else {
      error.value = response.message || '验证码发送失败'
    }
  } catch (err) {
    error.value = '验证码发送失败，请检查网络连接'
    console.error('Send code error:', err)
  } finally {
    codeButtonLoading.value = false
  }
}

const verifyPhone = async () => {
  error.value = ''

  // Validate phone
  if (!validation.validatePhone(form.value.phone)) {
    error.value = validation.getPhoneErrorMessage()
    return
  }

  // Validate code
  if (!validation.validateCode(form.value.code)) {
    error.value = validation.getCodeErrorMessage()
    return
  }

  // Move to step 2 (client-side, code verification happens when resetting password)
  step.value = 2
}

const resetPassword = async () => {
  error.value = ''
  passwordErrors.value = []

  // Validate password
  const passwordValidation = validation.validatePassword(form.value.newPassword)
  if (!passwordValidation.isValid) {
    passwordErrors.value = passwordValidation.errors
    error.value = passwordValidation.errors[0]
    return
  }

  // Validate password match
  if (!validation.validatePasswordMatch(form.value.newPassword, form.value.confirmPassword)) {
    error.value = validation.getPasswordMatchErrorMessage()
    return
  }

  loading.value = true
  try {
    const response = await authApi.resetPassword(
      form.value.phone,
      form.value.code,
      form.value.newPassword
    )

    if (response.code === 1) {
      // Password reset successful - redirect to login with password tab
      router.push({ path: '/login', query: { method: 'password' } })
    } else {
      error.value = response.message || '密码重置失败'
      // If code expired, go back to step 1
      if (response.message && response.message.includes('过期')) {
        step.value = 1
      }
    }
  } catch (err) {
    error.value = '密码重置失败，请检查网络连接'
    console.error('Reset password error:', err)
  } finally {
    loading.value = false
  }
}

const goBackStep = () => {
  step.value = 1
  error.value = ''
  form.value.newPassword = ''
  form.value.confirmPassword = ''
  passwordErrors.value = []
}

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="auth-container">
    <div class="auth-card">
      <h2 class="auth-title">
        {{ step === 1 ? '找回密码' : '重置密码' }}
      </h2>

      <!-- Error Message -->
      <div v-if="error" class="error-message">{{ error }}</div>

      <!-- Step 1: Verify Phone -->
      <form v-if="step === 1" @submit.prevent="verifyPhone" class="auth-form">
        <p class="instruction">请输入您的注册手机号和收到的验证码。</p>
        
        <div class="form-group">
          <label for="phone">手机号码</label>
          <div class="input-wrapper">
             <span class="prefix">+86</span>
             <input
              type="tel"
              id="phone"
              v-model="form.phone"
              required
              placeholder="请输入手机号码"
              maxlength="11"
              :disabled="loading || codeButtonLoading"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="code">验证码</label>
          <div class="code-wrapper">
            <input
              type="text"
              id="code"
              v-model="form.code"
              required
              placeholder="6位数字验证码"
              maxlength="6"
              :disabled="loading"
            />
            <button
              type="button"
              class="btn-code"
              @click="sendCode"
              :disabled="countdown > 0 || codeButtonLoading || loading"
            >
              {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
            </button>
          </div>
        </div>

        <button type="submit" class="btn-submit" :disabled="loading">
          {{ loading ? '正在验证...' : '下一步' }}
        </button>
      </form>

      <!-- Step 2: Set New Password -->
      <form v-if="step === 2" @submit.prevent="resetPassword" class="auth-form">
        <p class="instruction">请为您的账号设置一个新的密码。</p>

        <div class="form-group">
          <label for="newPassword">新密码</label>
          <input
            type="password"
            id="newPassword"
            v-model="form.newPassword"
            required
            placeholder="至少8位，包含大小写字母和数字"
            :disabled="loading"
          />
          <div v-if="passwordErrors.length > 0" class="password-hints">
            <div v-for="(err, idx) in passwordErrors" :key="idx" class="hint-item">
              {{ err }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">确认新密码</label>
          <input
            type="password"
            id="confirmPassword"
            v-model="form.confirmPassword"
            required
            placeholder="请再次输入密码"
            :disabled="loading"
          />
        </div>

        <button type="submit" class="btn-submit" :disabled="loading">
          {{ loading ? '正在重置...' : '重置密码' }}
        </button>
        <button type="button" class="btn-secondary" @click="goBackStep" :disabled="loading">
          返回上一步
        </button>
      </form>

      <div class="auth-footer">
        <router-link to="/login">返回登录</router-link>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-container {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f6f9;
  padding: 40px 20px;
}

.auth-card {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
  border-top: 4px solid #005eb8;
}

.auth-title {
  font-size: 24px;
  margin-bottom: 10px;
  color: #1a1a1a;
  text-align: center;
}

.error-message {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  color: #d84315;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
}

.instruction {
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.password-hints {
  background-color: #fef5f0;
  border: 1px solid #ffe7da;
  border-radius: 4px;
  margin-top: 6px;
  padding: 8px 12px;
}

.hint-item {
  font-size: 12px;
  color: #d84315;
  margin: 4px 0;
}

.hint-item:first-child {
  margin-top: 0;
}

.hint-item:last-child {
  margin-bottom: 0;
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.prefix {
  background: #f5f5f5;
  padding: 10px 12px;
  font-size: 14px;
  color: #666;
  border-right: 1px solid #ccc;
}

.input-wrapper input {
  border: none;
  flex: 1;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}

.input-wrapper:focus-within {
  border-color: #005eb8;
}

.form-group > input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}
.form-group > input:focus {
  border-color: #005eb8;
}

.code-wrapper {
  display: flex;
  gap: 10px;
}

.code-wrapper input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.code-wrapper input:focus {
  border-color: #005eb8;
}

.btn-code {
  padding: 0 16px;
  background: #eef4fb;
  color: #005eb8;
  border: 1px solid #cce0f5;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 100px;
  transition: all 0.2s;
}

.btn-code:hover:not(:disabled) {
  background: #dbe9f6;
}

.btn-code:disabled {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
  margin-top: 10px;
}

.btn-submit:hover {
  background-color: #004a94;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  width: 100%;
  padding: 12px;
  background-color: white;
  color: #005eb8;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
  margin-top: 10px;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #f0f8ff;
  border-color: #005eb8;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.auth-footer a {
  color: #005eb8;
  text-decoration: none;
  font-weight: 600;
}

.auth-footer a:hover {
  text-decoration: underline;
}
</style>